import {
    AfterViewInit,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ClassNames } from '../../../constants/class-names.constants';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { IChecklistItem } from '../../../models/common';
import { Utilities } from '../../../utilities/utilities.helper';
import { BaseFormControlComponent } from '../base-form-control.component';
import { memoize } from '../../../utilities/memoize';
import { debounceTime } from 'rxjs/operators';

/**
 * LSQ MultiSelect
 * @group Forms
 * @component MultiSelect
 *
 * @description Renders a multi select dropdown
 * @export
 * @class LsqMultiSelectComponent
 * @extends {BaseFormControlComponent<any>}
 * @implements {OnInit}
 */

@Component({
    selector: 'lsq-multi-select',
    template: `
        <div
            class="form-group select-group"
            (clickOutside)="handleBlur()"
            [ngClass]="[
                parentClass,
                basicLabel ? 'has-basic-label' : '',
                placeholder ? 'has-placeholder' : '',
                innerLabel ? 'has-inner-label' : '',
                innerLeftLabel ? 'has-inner-left-label' : '',
                floatLabel ? 'has-float-label' : '',
                dropUp ? 'drop-up' : '',
                !compact ? 'form-group select-group' : ''
            ]"
        >
            <div
                class="lsq-select-input"
                [ngClass]="{
                    'is-expanded': isExpanded,
                    disabled: disabled,
                    compact: compact
                }"
                (focus)="handleFocus()"
                [id]="createId()"
                [attr.tabindex]="'0'"
            >
                <label
                    *ngIf="label && basicLabel"
                    class="basic-label"
                    [attr.for]="createId()"
                    [ngClass]="labelClass"
                    (click)="toggleOptionsDropdown()"
                    >{{ label }}</label
                >

                <div
                    *ngIf="!compact"
                    class="form-control input-group lsq-select-viewport"
                    [ngClass]="[inputClass, isFilledClass(), isFocused ? 'is-focused' : '']"
                    aria-live="polite"
                    (click)="toggleOptionsDropdown()"
                >
                    <label
                        *ngIf="label && innerLeftLabel && !basicLabel"
                        [attr.for]="createId()"
                        [ngClass]="labelClass"
                        (click)="toggleOptionsDropdown()"
                        >{{ label }}</label
                    >

                    <span
                        *ngIf="displayNullOptionSelected() && !placeholder"
                        class="lsq-select-viewport-text null-option-selected"
                    >
                        {{ label }}
                    </span>

                    <span
                        *ngIf="displayNullOptionSelected() && !!placeholder"
                        class="lsq-select-viewport-text null-option-selected text-gray-600"
                    >
                        {{ placeholder }}
                    </span>

                    <span
                        *ngIf="!isFilledClass() && !displayNullOptionSelected() && !!placeholder"
                        class="lsq-select-viewport-text null-option-selected text-gray-600"
                    >
                        {{ placeholder }}
                    </span>

                    <span
                        class="lsq-select-viewport-text text-truncate"
                        *ngIf="displaySelectedOption()"
                        >{{ renderCollapsedLabel() }}</span
                    >

                    <lsq-icon
                        *ngIf="arrow"
                        class="chevron-icon"
                        [ngClass]="{
                            black: !hasDarkBackground(),
                            white: hasDarkBackground()
                        }"
                        [type]="icons.ChevronDown"
                        [color]="hasDarkBackground() ? color.White : color.Black"
                    >
                    </lsq-icon>
                </div>

                <div
                    *ngIf="compact"
                    class="lsq-select-input-compact"
                    (click)="toggleOptionsDropdown()"
                >
                    <span
                        *ngIf="displayNullOptionSelected() && !placeholder"
                        class="lsq-select-viewport-text null-option-selected"
                    >
                        {{ label }}
                    </span>

                    <span
                        *ngIf="displayNullOptionSelected() && !!placeholder"
                        class="lsq-select-viewport-text null-option-selected text-gray-600"
                    >
                        {{ placeholder }}
                    </span>

                    <span
                        *ngIf="!isFilledClass() && !displayNullOptionSelected() && !!placeholder"
                        class="lsq-select-viewport-text null-option-selected text-gray-600"
                    >
                        {{ placeholder }}
                    </span>

                    <span
                        class="lsq-select-viewport-text text-truncate"
                        *ngIf="displaySelectedOption()"
                    >
                        {{ renderCollapsedLabel() }}
                    </span>

                    <div class="triangle" [ngClass]="{ up: isExpanded, down: !isExpanded }"></div>
                </div>

                <label
                    *ngIf="label && !innerLeftLabel && !basicLabel"
                    [attr.for]="createId()"
                    [ngClass]="labelClass"
                    (click)="toggleOptionsDropdown()"
                >
                    {{ label }}
                </label>

                <div
                    *ngIf="isExpanded"
                    class="lsq-dropdown-container lsq-select-options-dropdown-container"
                    [ngClass]="{
                        'no-label': basicLabel && (label == null || label === ''),
                        'no-caret': !caret,
                        compact: compact
                    }"
                >
                    <span
                        *ngIf="caret"
                        class="dropdown-triangle"
                        [ngClass]="{ bottom: dropUp }"
                    ></span>
                    <div class="lsq-select-scroll-container" style="overflow: auto;">
                        <form [formGroup]="searchFormGroup">
                            <lsq-input
                                [placeholder]="'Search'"
                                formControlName="searchList"
                                [name]="'searchList'"
                                [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                                [group]="searchFormGroup"
                            >
                            </lsq-input>
                        </form>
                        <ng-scrollbar>
                            <ul class="lsq-select-options-dropdown list-unstyled">
                                <li id="max-results-disclaimer">
                                    <p *ngIf="!filteredOptionsAndIndex.length">
                                        No results were found.
                                    </p>
                                </li>
                                <li *ngIf="!searchTerm" class="lsq-select-option">
                                    <lsq-checkbox
                                        [id]="'all-option'"
                                        [label]="selectAllText || 'All'"
                                        [truncateLabel]="true"
                                        [truncateLabelTitle]="true"
                                        [ngModel]="selectAll"
                                        (ngModelChange)="handleAllChange($event)"
                                    ></lsq-checkbox>
                                </li>

                                <li
                                    *ngFor="
                                        let optionObject of filteredOptionsAndIndex.slice(
                                            0,
                                            pagesLoaded * PAGE_SIZE
                                        )
                                    "
                                    class="lsq-select-option"
                                >
                                    <lsq-checkbox
                                        [id]="
                                            createCheckboxId(
                                                optionObject.option.label,
                                                optionObject.index
                                            )
                                        "
                                        [label]="optionObject.option.label"
                                        [truncateLabel]="true"
                                        [truncateLabelTitle]="true"
                                        [ngModel]="indexCheckedMap.get(optionObject.index)"
                                        (ngModelChange)="updateModel($event, optionObject.index)"
                                    ></lsq-checkbox>
                                </li>
                                <li
                                    *ngIf="filteredOptionsAndIndex.length > pagesLoaded * PAGE_SIZE"
                                    id="load-more-results-button"
                                >
                                    <div>
                                        <lsq-button
                                            (buttonClick)="loadNewPage()"
                                            appearance="transparent"
                                            >Load more results</lsq-button
                                        >
                                    </div>
                                </li>
                            </ul>
                        </ng-scrollbar>
                    </div>
                </div>
            </div>
            <h6
                class="input-error-message text-uppercase font-weight-bold"
                *ngIf="showErrorMessage()"
            >
                {{ getErrorMessage() }}
            </h6>
        </div>
    `,
    styleUrls: ['./lsq-multi-select.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqMultiSelectComponent),
            multi: true
        }
    ]
})
export class LsqMultiSelectComponent extends BaseFormControlComponent<any>
    implements AfterViewInit, OnInit, ControlValueAccessor {
    @Input() public options: IChecklistItem[] = [];
    @Input() public label: string;
    @Input() public name?: string;
    @Input() public placeholder: string;
    @Input() public compact = false;
    @Input() public basicLabel = false;
    @Input() public floatLabel = true;
    @Input() public innerLabel = false;
    @Input() public innerLeftLabel = false;
    @Input() public arrow = true;
    @Input() public caret = false;
    @Input() public parentClass = '';
    @Input() public labelClass = '';
    @Input() public inputClass = '';
    @Input() public hideLabel = false;
    @Input() public dropUp = false;
    @Input() public disabled = false;
    @Input() public selectAllText?: string;
    @Input() public initializeWithAllSelected = false;
    @Output() public blur = new EventEmitter<Event>();

    public isExpanded = false;
    public isFocused = false;
    public icons = Icons;
    public color = Color;
    public searchFormGroup = new FormGroup({
        searchList: new FormControl()
    });
    public selectAll = false;
    public indexCheckedMap = new Map<number, boolean>();
    public searchTerm = '';
    public optionsAndIndex: Array<{ index: number; option: IChecklistItem }>;
    public filteredOptionsAndIndex: Array<{ index: number; option: IChecklistItem }>;
    public readonly PAGE_SIZE = 100;
    public pagesLoaded = 1;

    public ngOnInit(): void {
        this.buildOptions();

        if (this.basicLabel) {
            this.floatLabel = false;
            this.innerLabel = false;
        }

        if (this.hideLabel) {
            this.labelClass += ` ${ClassNames.ScreenReaderOnly}`;
        }

        if (this.innerLabel || this.innerLeftLabel) {
            this.floatLabel = false;
        }

        if (this.disabled) {
            this.handleBlur();
        }

        this.options.forEach((option, index) => {
            this.indexCheckedMap.set(index, this.getControl()?.value?.includes(option.value));
        });

        this.syncSelectAllCheckbox();

        this.searchFormGroup
            .get('searchList')
            .valueChanges.pipe(debounceTime(100))
            .subscribe((value) => {
                this.searchTerm = value;
                this.pagesLoaded = 1;
                this.filteredOptionsAndIndex = this.optionsAndIndex.filter((object) =>
                    object?.option?.label.toLowerCase().includes(this.searchTerm?.toLowerCase())
                );
            });
    }

    public ngAfterViewInit(): void {
        if (
            this.initializeWithAllSelected &&
            (!this.getControl()?.value || !this.getControl()?.value?.length)
        ) {
            // Wrap in `executeAfterRender` to avoid `ExpressionChangedAfterItHasBeenCheckedError`.
            Utilities.executeAfterRender(() => this.handleAllChange(true, true));
        }
    }

    @memoize()
    public createCheckboxId(label: string, index: number, suffix: string = ''): string {
        label = label.replace(/\s/g, '-').toLowerCase();
        return label + (suffix ? '-' + suffix : '') + '--' + index.toString();
    }

    public createId(): string {
        if (this.name) {
            return this.name;
        }
        if (!this.label) {
            return '';
        }
        return this.label.replace(/\s/g, '-').toLowerCase();
    }

    public updateModel(checked: boolean, index: number): void {
        this.indexCheckedMap.set(index, checked);
        this.syncSelectAllCheckbox();
        this.handleChange(
            this.options.filter((_, i) => this.indexCheckedMap.get(i)).map(({ value }) => value)
        );
    }

    public handleAllChange(value: boolean, skipEmittingEvent = false): void {
        this.searchFormGroup.get('searchList').reset('');
        this.selectAll = value;
        this.options.forEach((_, index) => this.indexCheckedMap.set(index, value));
        const newValue = this.options
            .filter((_, i) => this.indexCheckedMap.get(i))
            .map(({ value }) => value);
        this.model = newValue;

        if (!skipEmittingEvent) {
            this.handleChange(newValue);
        }
    }

    public loadNewPage(): void {
        this.pagesLoaded++;
    }

    private syncSelectAllCheckbox(): void {
        let allChecked = true;
        this.indexCheckedMap.forEach((value) => (allChecked = allChecked && value));
        if (allChecked) {
            this.selectAll = true;
        } else {
            this.selectAll = false;
        }
    }

    private buildOptions(): void {
        const newOptions: IChecklistItem[] = [];

        function recursivelyAddOptions(options: IChecklistItem[], areChildren?: boolean): void {
            options.forEach((option) => {
                if (areChildren) {
                    option.isChild = true;
                }
                newOptions.push(option);
                if (option.children) {
                    recursivelyAddOptions([...option.children], true);
                    delete option.children;
                }
            });
        }

        // Create SelectOption objects out of string or number options.
        this.options = this.options.map(
            (option: any): IChecklistItem => {
                if (typeof option === 'object' && option !== null) {
                    return <IChecklistItem>option;
                } else {
                    let label = option;
                    if (typeof option === 'number') {
                        label = option.toString();
                    }
                    return {
                        label,
                        value: option
                    };
                }
            }
        );

        // Pull child options out into the options array.
        recursivelyAddOptions(this.options);
        this.options = [...newOptions];

        this.optionsAndIndex = this.options.map((option, index) => ({ index, option }));
        this.filteredOptionsAndIndex = this.optionsAndIndex;
    }

    public renderCollapsedLabel(): string {
        if (this.model?.length >= 1) {
            if (this.selectAll) {
                return this.selectAllText || 'All Items';
            }
            return `${this.model?.length} Option${this.model?.length > 1 ? 's' : ''} Selected`;
        }
        // Find the label of the selected item.
        return this.options.find((option) => option.value === this.model[0])?.label;
    }

    // This will set the 'is-focused-or-filled' class to style float labels or inner labels, but for selects, we really only want to test the 'isFilled' part. For simplicity's sake, we've kept the class name the same.
    public isFilledClass(): string {
        const isFilled = (): boolean => !!this.model?.length;

        return isFilled() ? ClassNames.IsFocusedOrFilled : '';
    }

    public toggleOptionsDropdown(expanded?: boolean): void {
        if (this.disabled) {
            return;
        }
        this.isExpanded = typeof expanded === 'undefined' ? !this.isExpanded : expanded;
        this.filteredOptionsAndIndex = this.optionsAndIndex;
    }

    public displayNullOptionSelected(): boolean {
        return this.hideLabel && !this.model?.length;
    }
    public displaySelectedOption(): boolean {
        return !!this.model?.length;
    }

    public hasDarkBackground(): boolean {
        return this.parentClass.indexOf(ClassNames.BackgroundDark) > -1;
    }

    public handleFocus(): void {
        this.isFocused = true;
    }

    public handleBlur(): void {
        Utilities.executeAfterRender(() => {
            this.isFocused = false;
            this.isExpanded = false;
            this.pagesLoaded = 1;
            this.searchFormGroup.get('searchList').reset('');
            this.blur.emit();
        });
    }

    // Called directly from FormGroup
    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
